import React, { useState, useEffect, useCallback, memo, Fragment, useRef } from "react";
import PropTypes from "prop-types";
import { BaseWidget } from "../shared";
import { SortableContainer, SortableElement, arrayMove } from "react-sortable-hoc";
import { List, ListItem, ListItemText, ListItemIcon, SvgIcon } from "@mui/material";
import { Reorder, Visibility } from "@mui/icons-material";
import { getTranslation } from "orion-components/i18n";
import { useSelector, useDispatch } from "react-redux";
import * as actionCreators from "./floorPlanWidgetActions";
import { floorPlanSelector } from "orion-components/Map/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import { getWidgetState, getSelectedContextData } from "orion-components/Profiles/Selectors";
import { updatePersistedState } from "orion-components/AppState/Actions";
import { persistedState } from "orion-components/AppState/Selectors";
import { removeFeed } from "orion-components/ContextualData/Actions";
import { startFloorPlanCameraStream, startFloorPlanDevicesStream } from "orion-components/ContextualData/Actions";
import { mdiFormatListBulleted } from "@mdi/js";
import { mdiApps } from "@mdi/js";
import ThumbnailViewer from "./ThumbnailViewer";
import { sitePlanSelector } from "orion-components/GlobalData/Selectors";
import { streamFloorPlanCompositeDevices, unsubscribe } from "../../../ContextualData/contextStreaming";

const propTypes = {
	handleSelect: PropTypes.func.isRequired,
	selectedFloor: PropTypes.object,
	setFloorOrder: PropTypes.func.isRequired,
	dir: PropTypes.string
};

const defaultProps = {
	selectedId: null,
	dir: "rtl"
};

const SortableItem = SortableElement(({ item, selected, onSelect, dir }) => {
	const styles = {
		listItem: {
			zIndex: 999,
			backgroundColor: selected && selected.id === item.id ? "#1688bd" : "#494D53",
			marginBottom: 8,
			...(dir === "rtl" && { flexDirection: "row-reverse" })
		},
		listItemIcon: {
			...(dir === "rtl" && { justifyContent: "end" })
		},
		listItemText: {
			overflow: "hidden",
			whiteSpace: "nowrap",
			textOverflow: "ellipsis",
			color: "white",
			textAlign: dir == "rtl" ? "right" : "left"
		}
	};
	return (
		<ListItem
			data-testid="floor-plan-list-item"
			button
			onClick={() => {
				onSelect(item);
			}}
			style={styles.listItem}
		>
			<ListItemIcon style={styles.listItemIcon}>
				<Reorder />
			</ListItemIcon>
			<ListItemText style={styles.listItemText} primary={item.name} primaryTypographyProps={{ noWrap: true }} />
			{selected && selected.id === item.id && (
				<ListItemIcon style={styles.listItemIcon}>
					<Visibility />
				</ListItemIcon>
			)}
		</ListItem>
	);
});

const SortableList = SortableContainer(({ items, selected, onSelect, dir }) => {
	return (
		<List data-testid="floor-plan-list">
			{items.map((item, index) => (
				<SortableItem key={index} index={index} item={item} selected={selected} onSelect={onSelect} dir={dir} data-testid="orion-components-profiles-widgets-floor-plan-widget-floor-plan-widget-sortable-item" />
			))}
		</List>
	);
});

const FloorPlanWidget = ({
	id,
	handleSelect,
	clearFloorPlan,
	selectFloorPlan,
	setFloorPlans,
	order,
	contextId,
	getSelectedFloorPlan
}) => {
	const dispatch = useDispatch();

	const { setFloorOrder, syncSelectFloorPlan, syncClearFloorPlan } = actionCreators;
	const subscriptionsFromContext = useSelector(state => getSelectedContextData(state)(contextId, "subscriptions"));
	const selectedFloor = useSelector(state => floorPlanSelector(state).selectedFloor || {});
	const fpDevicesSubscriptions = subscriptionsFromContext?.floorPlanDevices || false;
	const fpCameraSubscriptions = subscriptionsFromContext?.floorPlanCameras || false;
	const fpCompositeDevicesSubscriptions = subscriptionsFromContext?.floorPlanCompositeDevices || false;
	const dir = useSelector(state => getDir(state));
	const enabled = useSelector(state => getWidgetState(state)(id, "enabled")) ?? false;
	const cameras = useSelector(state => getWidgetState(state)("cameras", "enabled"));
	const floorPlans = useSelector(state => state.globalData?.floorPlans);
	const entity = useSelector(state => getSelectedContextData(state)(contextId, "entity"));
	const facilityFeedId = entity?.feedId || "";
	const facilityId = entity?.id || "";
	const floorPlanView = useSelector(state => persistedState(state).floorPlanView);
	const selectedFloors = useSelector(state => persistedState(state).selectedFloors || {});
	const sitePlan = useSelector(state => sitePlanSelector(state) || {});
	const appId = useSelector(state => state.application.appId);

	const activeFpSubscriptions = useRef(new Set());
	const prevSelectedFloor = useRef(null);
	const floorPlanEntitySubIds = useRef({ floorPlanCameras: "", floorPlanDevices: "", floorPlanCompositeDevices: "" });

	// Sync persisted floor plan selection back into state when facility loads
	// This ensures the UI shows the correct selection after refresh or navigation
	// Only applies to apps that use persisted selectedFloors (like map-app)
	useEffect(() => {
		if (!facilityId || !enabled) return;

		// Check if this facility has an entry in selectedFloors (even if null)
		const hasPersistedEntry = facilityId in selectedFloors;
		const persistedFloor = selectedFloors[facilityId];

		// Only sync if this app uses persisted floor plans (has an entry for this facility)
		if (hasPersistedEntry) {
			// If persisted is null and we have a selectedFloor, clear it
			if (persistedFloor === null && selectedFloor?.id) {
				dispatch(syncClearFloorPlan());
			}
			// If persisted has a floor plan and it doesn't match current selection, restore it
			else if (persistedFloor && persistedFloor.id !== selectedFloor?.id) {
				dispatch(syncSelectFloorPlan(persistedFloor));
			}
		}
	}, [facilityId, enabled, selectedFloors, selectedFloor, dispatch]);

	useEffect(() => {
		if (!facilityId) return;

		const subscriptionsMap = [
			{
				enabled: cameras,
				subscription: fpCameraSubscriptions,

				startAction: startFloorPlanCameraStream,
				type: "floorPlanCameras"
			},
			{
				enabled: true,
				subscription: fpDevicesSubscriptions,

				startAction: startFloorPlanDevicesStream,
				type: "floorPlanDevices"
			},
			{
				enabled: true,
				subscription: fpCompositeDevicesSubscriptions,

				startAction: streamFloorPlanCompositeDevices,
				type: "floorPlanCompositeDevices"
			}
		];

		subscriptionsMap.forEach(({ enabled, subscription, startAction, type }) => {
			if (!enabled || !selectedFloor?.id) return;

			const subKey = `${facilityId}-${selectedFloor.id}-${type}`;
			const prevSubKey = prevSelectedFloor.current
				? `${facilityId}-${prevSelectedFloor.current.id}-${type}`
				: null;

			// Remove subscriptions based on the previous selected floor

			if (prevSelectedFloor?.current?.id !== undefined && prevSelectedFloor?.current?.id !== selectedFloor?.id) {
				if (floorPlanEntitySubIds?.current?.[type]?.subscription) {
					dispatch(unsubscribe(floorPlanEntitySubIds?.current?.[type]?.subscription));
					activeFpSubscriptions.current.delete(prevSubKey);
				}
			}

			// Add new subscription if it's not already active
			if (!subscription && selectedFloor.facilityId === facilityId) {
				if (!activeFpSubscriptions.current.has(subKey)) {
					dispatch(startAction(facilityId, selectedFloor.id, "profile"));
					activeFpSubscriptions.current.add(subKey);
				}
			}
		});

		// Update the previous floor reference after processing
		prevSelectedFloor.current = selectedFloor || null;

		floorPlanEntitySubIds.current.floorPlanCameras = fpCameraSubscriptions;
		floorPlanEntitySubIds.current.floorPlanDevices = fpDevicesSubscriptions;
		floorPlanEntitySubIds.current.floorPlanCompositeDevices = fpCompositeDevicesSubscriptions;

		if (selectedFloor) {
			getSelectedFloorPlan(selectedFloor);
		}
	}, [
		selectedFloor,
		facilityId,
		cameras,
		fpCameraSubscriptions,
		fpDevicesSubscriptions,
		fpCompositeDevicesSubscriptions
	]);

	const [moving, setMoving] = useState(false);
	const [thumbnailView, setThumbnailView] = useState(true);

	const getFloors = floors => {
		return Object.values(floors).sort((a, b) => {
			if (a.order < b.order) {
				return -1;
			}
			if (a.order > b.order) {
				return 1;
			}
			return 0;
		});
	};
	const handleSortEnd = useCallback(
		({ oldIndex, newIndex }) => {
			const newItems = arrayMove(getFloors(floorPlans), oldIndex, newIndex);
			newItems.forEach((floor, index) => (floor.order = index + 1));
			dispatch(setFloorOrder(newItems));
			setMoving(false);
		},
		[floorPlans, setFloorOrder]
	);
	const handleSelectFloor = useCallback(
		floor => {
			if (floorPlanView) {
				selectSitePlan(floor);
			} else {
				if (selectedFloor) {
					if (selectedFloor.id === floor.id) {
						dispatch(clearFloorPlan(floor));
					} else {
						dispatch(removeFeed(selectedFloor.facilityId, "floorPlanCameras"));
						dispatch(removeFeed(selectedFloor.facilityId, "floorPlanDevices"));
						dispatch(removeFeed(selectedFloor.facilityId, "floorPlanCompositeDevices"));
						dispatch(handleSelect(floor, facilityFeedId));
					}
				} else {
					dispatch(handleSelect(floor, facilityFeedId));
				}
			}
		},
		[handleSelect, clearFloorPlan, selectedFloor, floorPlanView, sitePlan]
	);
	const handleLaunch = () => {
		window.open(`/facilities-app/#/entity/${contextId}`);
	};

	const customIcons = () => {
		return (
			<div>
				<SvgIcon
					style={{
						width: "15.4px",
						height: "13.2px",
						color: !thumbnailView ? "FFFFFF" : "#5B5C5D",
						cursor: "pointer"
					}}
					onClick={() => {
						setThumbnailView(false);
					}} data-testid="orion-components-profiles-widgets-floor-plan-widget-floor-plan-widget-svg-icon"
				>
					<path d={mdiFormatListBulleted} />
				</SvgIcon>
				<SvgIcon
					style={{
						width: "15.4px",
						height: "13.2px",
						color: thumbnailView ? "FFFFFF" : "#5B5C5D",
						cursor: "pointer",
						marginLeft: "12px"
					}}
					onClick={() => {
						setThumbnailView(true);
					}} data-testid="orion-components-profiles-widgets-floor-plan-widget-floor-plan-widget-svg-icon-2"
				>
					<path d={mdiApps} />
				</SvgIcon>
			</div>
		);
	};

	const selectSitePlan = floor => {
		dispatch(
			updatePersistedState(appId, "selectedSitePlan", {
				selectedSitePlan: sitePlan.id === floor.id ? null : floor?.id
			})
		);
	};

	return !enabled ? (
		<div />
	) : (
		<BaseWidget
			title={getTranslation("global.profiles.widgets.floorPlanWidget.floorPlans")}
			order={order}
			handleLaunch={handleLaunch}
			dir={dir}
			showIcons={customIcons() || <Fragment />}
		>
			{thumbnailView ? (
				<ThumbnailViewer
					floorPlans={getFloors(floorPlans)}
					selectedFloor={floorPlanView ? sitePlan : selectedFloor}
					handleSelectFloor={handleSelectFloor}
				/>
			) : (
				<SortableList
					items={getFloors(floorPlans)}
					onSortEnd={handleSortEnd}
					distance={12}
					selected={moving ? null : selectedFloor}
					onSelect={handleSelectFloor}
					onSortStart={() => setMoving(true)}
					dir={dir} data-testid="orion-components-profiles-widgets-floor-plan-widget-floor-plan-widget-sortable-list"
				/>
			)}
		</BaseWidget>
	);
};

FloorPlanWidget.propTypes = propTypes;
FloorPlanWidget.defaultProps = defaultProps;

export default memo(FloorPlanWidget);
